import { AsyncPipe, NgStyle } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { OnboardingDialogResult } from '@app/+authenticated/shared/onboarding/onboarding-dialog/onboarding-dialog.model';
import { OnboardingService } from '@app/+onboarding/onboarding.service';
import { BoldPipe } from '@app/pipes/bold.pipe';
import { KnowledgeBaseArticleLinkModule } from '@app/pipes/knowledge-base-article-link.module';
import { getAuthenticatedUserEmail } from '@app/reducers/auth/auth.service';
import { UserGuideType } from '@app/reducers/orm/user-guide';
import { TrackingService } from '@app/services/tracking.service';
import { SmbHospitalityOnboardingChecklistStep } from '@app/shared/onboarding/onboarding-checklist.model';
import { OnboardingChecklistService } from '@app/shared/onboarding/onboarding-checklist.service';
import { SbFormFieldComponent } from '@app/shared/sb-lib/forms/sb-form-field.component';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AppState } from '@reducers/index';
import {
  ButtonThemePalette,
  DIALOG_DATA,
  DialogActivationComponent,
  DialogRef,
  InputDirective,
  RadioBadgeComponent,
  RadioBadgeGroupComponent,
} from '@sb/ui';
import { finalize, switchMap } from 'rxjs';

import { TranslationParamsPipe } from '../../../pipes/translation-params.pipe';
import { OnboardingChapterModule } from '../components/chapter.module';
import { PayrollDialogStep, payrollDialogStepProgressMap } from './payroll.model';
import { OnboardingPayrollService } from './payroll.service';

type PayrollToolOption =
  | 'AFAS Software'
  | 'Buddee'
  | 'Connexie'
  | 'Datev'
  | 'e-UUR'
  | 'Easylon'
  | 'Employes'
  | 'Exact Online'
  | 'HR & Salaris Gemak'
  | 'Loket.nl'
  | 'SD Worx'
  | 'Visma'
  | 'Nmbrs'
  | 'other'
  | 'unknown_provider'
  | 'none';

@Component({
  selector: 'payroll-dialog',
  standalone: true,
  imports: [
    DialogActivationComponent,
    FormsModule,
    NgStyle,
    TranslateModule,
    TranslationParamsPipe,
    OnboardingChapterModule,
    RadioBadgeGroupComponent,
    RadioBadgeComponent,
    KnowledgeBaseArticleLinkModule,
    InputDirective,
    SbFormFieldComponent,
    AsyncPipe,
    BoldPipe,
  ],
  providers: [OnboardingPayrollService],
  templateUrl: './payroll-dialog.component.html',
})
export class PayrollDialogComponent {
  public ButtonThemePalette = ButtonThemePalette;

  public PayrollDialogStep = PayrollDialogStep;
  public currentStep = PayrollDialogStep.TEASER;
  public currentStepProgress = payrollDialogStepProgressMap[this.currentStep];
  public steps = 3;
  public nextButtonLoading = false;
  public skipButtonLoading = false;
  public email$ = this.store.select(getAuthenticatedUserEmail);

  public selectedPayrollTool: PayrollToolOption;
  public payrollToolOptions: { value: PayrollToolOption; label?: string }[] = [
    { value: 'AFAS Software' },
    { value: 'Buddee' },
    { value: 'Connexie' },
    { value: 'Datev' },
    { value: 'e-UUR' },
    { value: 'Easylon' },
    { value: 'Employes' },
    { value: 'Exact Online' },
    { value: 'HR & Salaris Gemak' },
    { value: 'Loket.nl' },
    { value: 'SD Worx' },
    { value: 'Visma' },
    { value: 'Nmbrs' },
    { value: 'other', label: this.translate.instant('Other tool') },
    { value: 'unknown_provider', label: this.translate.instant("I don't know which tool") },
  ];
  public payrollTool: string;
  public skipEmail = false;

  private eventTracked = false;

  public constructor(
    @Inject(DIALOG_DATA) public readonly eventData,
    private readonly dialogRef: DialogRef,
    private readonly payrollService: OnboardingPayrollService,
    private readonly store: Store<AppState>,
    private readonly translate: TranslateService,
    private readonly trackingService: TrackingService,
    private readonly onboardingService: OnboardingService,
    private readonly onboardingChecklist: OnboardingChecklistService,
  ) {}

  public get chapterCTAs() {
    return this.onboardingService.chapterCTAs;
  }

  public onSelectPayrollToolNext() {
    switch (this.selectedPayrollTool) {
      case 'other':
        this.goToStep(PayrollDialogStep.OTHER_TOOL);
        break;
      case 'unknown_provider':
        this.goToStep(PayrollDialogStep.UNKNOWN_TOOL);
        break;
      default:
        this.goToStep(PayrollDialogStep.SPECIFIC_TOOL);
    }
  }

  public onNoPayrollToolSelected() {
    this.selectedPayrollTool = 'none';
    this.goToStep(PayrollDialogStep.NO_TOOL);
  }

  public goToStep(step: PayrollDialogStep, skipEmail?: boolean, clickedButton: 'next' | 'skip' = 'next') {
    if (this.currentStep === PayrollDialogStep.TEASER && !this.eventTracked) {
      this.trackingService.event('Onboarding Payroll Started', this.eventData);
      this.eventTracked = true;
    }

    if (step === PayrollDialogStep.SUMMARY) {
      this.skipEmail = skipEmail ?? false;
      this.savePreferredPayrollIntegration(clickedButton);
    } else {
      this.currentStep = step;
      this.currentStepProgress = payrollDialogStepProgressMap[this.currentStep];
    }
  }

  public goBack() {
    const intermediateSteps = [
      PayrollDialogStep.OTHER_TOOL,
      PayrollDialogStep.UNKNOWN_TOOL,
      PayrollDialogStep.SPECIFIC_TOOL,
      PayrollDialogStep.NO_TOOL,
    ];
    if (intermediateSteps.includes(this.currentStep)) {
      this.goToStep(PayrollDialogStep.SELECT_PAYROLL_TOOL);
      return;
    }

    this.goToStep(this.currentStep - 1);
  }

  public close(result: OnboardingDialogResult) {
    this.dialogRef.close(result);
  }

  private savePreferredPayrollIntegration(clickedButton: 'next' | 'skip') {
    this.nextButtonLoading = clickedButton === 'next';
    this.skipButtonLoading = clickedButton === 'skip';

    let preferredPayrollIntegration: string = this.selectedPayrollTool;

    if (this.selectedPayrollTool === 'other') {
      preferredPayrollIntegration = clickedButton === 'skip' ? 'not_specified' : this.payrollTool;
    }

    void this.payrollService
      .savePreferredPayrollIntegration(preferredPayrollIntegration, this.skipEmail)
      .pipe(
        switchMap(() =>
          this.onboardingChecklist.completeStep(
            UserGuideType.SELF_ONBOARDING_CHECKLIST,
            SmbHospitalityOnboardingChecklistStep.PAYROLL,
          ),
        ),
        finalize(() => {
          this.nextButtonLoading = false;
          this.skipButtonLoading = false;
        }),
      )
      .subscribe(() => {
        this.trackingService.event('Onboarding Payroll Completed', this.eventData);
        this.currentStep = PayrollDialogStep.SUMMARY;
      });
  }
}
